import { StepTypeEnum } from "./processingActivity/ProcessingActivity.types";
import { useLocation } from "react-router";
import { RiskAssessmentArea, RiskComplianceArea } from "./riskAssessment/riskAssessments/RiskAssessments.types";
import { UrlToPathObject } from "@dotlegal/dotlegal-ui-components";
import { LegalEntityScope } from "./legalEntity/legalEntities/LegalEntities.types";
import { isNullOrWhitespace } from "./common/stringOperations";
import { LegalEntityAuditTabs } from "./legalEntity/LegalEntity.types";
import { getSettings } from "./common/settingsProvider.ts";
import { AnnualWheelMonthEnum } from "./annualWheel/AnnualWheel.types.ts";
import { useUserContext } from "./auth/userContextProvider/UserContextProvider.tsx";
import { ContractOverviewTabEnum } from "./contract/contractOverview/ContractOverview.types.ts";
import { ContractListDeepLink, toContractListSearch } from "./contract/ContractListDeepLink.ts";
import { TemplateEntityType } from "./entityTemplates/EntityTemplate.types.ts";
import { EntityType } from "@common/Common.types";
import { RiskAreaTab, riskAreaTabToQuery } from "./risk/riskArea/RiskArea.types.ts";
import { RiskOperationalSection } from "./risk/riskOperationalOverview/RiskOperationalOverview.types.ts";
import { SeverityLevelEnum } from "./incidentLog/IncidentLogDialog.types.ts";
import { DataProcessingTab, dataProcessingTabToQuery } from "./dataProcessing/DataProcessing.types.ts";

export function useUrlProvider() {
    const location = useLocation();
    const { customerId } = useUserContext();

    function getStepParams(stepNumber?: number, subStepNumber?: number, create?: boolean) {
        const searchParams = new URLSearchParams(location.search);
        if (stepNumber) searchParams.set("step", stepNumber.toString());
        if (subStepNumber) {
            searchParams.set("substep", subStepNumber.toString());
        }
        if (create) searchParams.set("create", "true");
        return `?${searchParams.toString()}`;
    }

    function getEditProcessingActivityUrl(processingActivityId: string, step?: StepTypeEnum, subStep?: StepTypeEnum, entityId?: string, create?: boolean) {
        const stepParams = getStepParams(step, subStep, create);
        const firstPart = `/privacy/processingactivity/${processingActivityId}/edit${stepParams}`;
        return UrlToPathObject(entityId ? firstPart + `#${entityId}` : firstPart);
    }

    function getEditProcessingActivityUrlCreateView(processingActivityId: string) {
        return getEditProcessingActivityUrl(processingActivityId, undefined, undefined, undefined, true);
    }

    function getProcessingActivityOverviewUrl(processingActivityId: string | undefined, tab?: number) {
        const s = `/privacy/processingactivity/${processingActivityId}/overview`;
        if (tab) {
            return s + `?tab=${tab}`;
        }
        return s;
    }

    function getProcessingActivityOverviewWithOpenCommenDialogUrl(processingActivityId: string) {
        return `/privacy/processingactivity/${processingActivityId}/overview?commentDialog=true`;
    }

    function getProcessingActivityValidationUrl(processingAcitivtyId: string) {
        return `/privacy/processingactivity/${processingAcitivtyId}/validate`;
    }

    function getSystemCreateViewUrl(systemId: string) {
        return getSystemEditUrl(systemId) + "?create=true";
    }

    function getGroupEntitiesUrl() {
        return "/privacy/group-entities";
    }

    // The list of everything the customer does not own. Its rows link to getLegalEntityUrl, so there is
    // deliberately no matching getExternalLegalEntityUrl.
    function getExternalLegalEntities() {
        return "/privacy/external-legal-entities";
    }

    function getLegalEntityUrl(legalEntityId: string, tab?: LegalEntityAuditTabs) {
        let route = `/privacy/legal-entities/${legalEntityId}`;

        if (tab !== undefined) {
            return route + `?tab=${tab}`;
        }

        return route;
    }

    function getGroupEntityUrl(legalEntityId: string | undefined, tab?: LegalEntityAuditTabs) {
        let route = `/privacy/group-entities/${legalEntityId}`;

        if (tab !== undefined) {
            return route + `?tab=${tab}`;
        }

        return route;
    }

    function getLegalEntityBaseUrl(scope: LegalEntityScope, legalEntityId: string) {
        return scope === LegalEntityScope.GroupEntity ? getGroupEntityUrl(legalEntityId) : getLegalEntityUrl(legalEntityId);
    }

    function getLegalEntityEditUrl(scope: LegalEntityScope, legalEntityId: string, queryParams?: string) {
        const url = `${getLegalEntityBaseUrl(scope, legalEntityId)}/edit`;

        return queryParams ? `${url}?${queryParams}` : url;
    }

    function getProcessingActivitiesOverviewUrl() {
        return "/privacy/processingactivities";
    }

    function getDataProcessingUrl(view: DataProcessingTab) {
        return `/privacy/processingactivities?view=${dataProcessingTabToQuery(view)}`;
    }

    function getProcessesUrl() {
        return "/privacy/processes";
    }

    function getProcessOverviewUrl(id: string, tab?: number) {
        const url = `/privacy/processes/${id}/overview`;
        return tab ? `${url}?tab=${tab}` : url;
    }

    function getProcessEditUrl(id: string, queryParams?: string) {
        const url = `/privacy/processes/${id}/edit`;
        return queryParams ? `${url}?${queryParams}` : url;
    }

    function getSystemsUrl() {
        return "/privacy/systems";
    }

    function getRecordUrl() {
        return "/privacy/record";
    }

    function getSignOutUrl() {
        return "/privacy/sign-out";
    }

    function getHelpUrl(link: string) {
        if (link) {
            return link;
        }

        return "https://help.dotlegal.com/en/knowledge-base";
    }

    function getSettingsUrl() {
        return "/privacy/settings";
    }

    function getBestPracticeDocumentTemplatesUrl() {
        return "/privacy/masterdata/best-practice-document-templates";
    }

    function getUsersUrl() {
        return "/privacy/users";
    }

    function getUserUrl(userId: string) {
        return `/privacy/user/${userId}`;
    }

    function getCustomersUrl() {
        return "/privacy/customers";
    }

    function getExternalUsersUrl() {
        return "/privacy/external-users";
    }

    function getChangeOrganizationUrl() {
        return "/privacy/change-organization";
    }

    function getMasterDataUrl() {
        return "/privacy/masterdata";
    }

    function getEmailCustomizationUrl() {
        return "/privacy/email-customization";
    }

    function getApiKeysUrl() {
        return "/privacy/api-keys";
    }

    function getAiAdministrationUrl() {
        return "/privacy/ai-administration";
    }

    function getForbiddenUrl() {
        return "/privacy/forbidden";
    }

    function getPurposesUrl() {
        return "/privacy/masterdata/purposes";
    }

    function getBusinessAreasUrl() {
        return "/privacy/masterdata/business-areas";
    }

    function getAnnualWheelActivityStandardTemplatesUrl() {
        return "/privacy/annual-wheel-activity-standard-templates";
    }

    function getAnnualWheelActivityTemplatesUrl() {
        return "/privacy/masterdata/annual-wheel-activity-templates";
    }

    function getSecurityMeasures() {
        return "/privacy/masterdata/security-measures";
    }

    function getCertificationsUrl() {
        return "/privacy/masterdata/certifications";
    }

    function getAreasUrl() {
        return "/privacy/masterdata/areas";
    }

    function getRiskAreasUrl() {
        return "/privacy/risk-settings/risk-areas";
    }

    function getRiskScenariosUrl() {
        return "/privacy/risk-settings/risk-scenarios";
    }

    function getConsequencesUrl() {
        return "/privacy/risk-settings/risk-consequences";
    }

    function getDeletionPeriods() {
        return "/privacy/masterdata/deletion-periods";
    }

    function getDataCategories() {
        return "/privacy/masterdata/data-categories";
    }

    function getDataTypesUrl() {
        return "/privacy/masterdata/data-types";
    }
    function getDataSubjectsUrl() {
        return "/privacy/masterdata/data-subjects";
    }

    function getNationalLawsUrl() {
        return "/privacy/masterdata/national-laws";
    }

    function getLegislationsUrl() {
        return "/privacy/masterdata/legislations";
    }

    function getHelpTextsUrl() {
        return "/privacy/masterdata/help-texts";
    }

    function getRiskAssessmentVersionsUrl() {
        return "/privacy/masterdata/risk-assessment-versions";
    }

    function getRiskAssessmentsUrl() {
        return "/privacy/risk-assessments";
    }

    function getRiskManagementReportUrl() {
        return "/privacy/risk-management-report";
    }

    function getObservationsUrl() {
        return "/privacy/observations";
    }

    function getRiskManagementReportPdfUrl(id: string) {
        return `/privacy/risk/management-report/${id}`;
    }

    function getRiskGovernanceOverviewPdfUrl(riskAreaId: string) {
        return `/privacy/risk/governance-overview-pdf/${riskAreaId}`;
    }

    function getRiskAreaUrl(riskAreaId: string, view: RiskAreaTab) {
        return `/privacy/risk/${riskAreaId}?view=${riskAreaTabToQuery(view)}`;
    }

    // Deep-links into the risk area's Operational tab with the filter pre-seeded from the URL. The operational
    // overview reads these once on arrival, then strips them (same mechanism as the mitigation deep links).
    // overAppetite is emitted explicitly (true AND false): the linked-risk tile's landing set is "not over
    // appetite themselves, but linked to something over it". notAssessed is phrased as the gap it links to, so
    // the operational overview seeds its Assessed filter to No.
    function getRiskOperationalOverviewUrl(
        riskAreaId: string,
        filter?: {
            overAppetite?: boolean;
            overAppetiteWithoutControls?: boolean;
            overAppetiteWithoutTreatment?: boolean;
            linkedRiskOverAppetite?: boolean;
            notAssessed?: boolean;
            // Which of the tab's two tables to open on. Both tables seed the same filter params, so this only
            // decides which one is on screen when the link lands.
            section?: RiskOperationalSection;
        }
    ) {
        const params = new URLSearchParams();
        if (filter?.overAppetite !== undefined) params.set("overAppetite", String(filter.overAppetite));
        if (filter?.overAppetiteWithoutControls) params.set("overAppetiteWithoutControls", "true");
        if (filter?.overAppetiteWithoutTreatment) params.set("overAppetiteWithoutTreatment", "true");
        if (filter?.linkedRiskOverAppetite) params.set("linkedRiskOverAppetite", "true");
        if (filter?.notAssessed) params.set("notAssessed", "true");
        if (filter?.section !== undefined) params.set("section", String(filter.section));
        const query = params.toString();
        return `/privacy/risk/${riskAreaId}${query ? `?${query}` : ""}`;
    }

    function getRiskAssessmentsOverviewUrl(riskAreaId: string, entityType: EntityType, entityId: string) {
        return `/privacy/risk/${riskAreaId}/${entityType}/${entityId}`;
    }

    // Deep-links into the risk area's Mitigation tab with the filter pre-seeded from the URL. The mitigation
    // overview reads these once on arrival (statuses/scenarioIds/entityIds are comma-joined), then strips them.
    function getRiskMitigationOverviewUrl(
        riskAreaId: string,
        filter?: {
            statuses?: Array<number>;
            scenarioIds?: Array<string>;
            entityIds?: Array<string>;
            withoutControls?: boolean;
            overdue?: boolean;
        }
    ) {
        const params = new URLSearchParams({ view: riskAreaTabToQuery(RiskAreaTab.Mitigation) });
        if (filter?.statuses?.length) params.set("statuses", filter.statuses.join(","));
        if (filter?.scenarioIds?.length) params.set("scenarioIds", filter.scenarioIds.join(","));
        if (filter?.entityIds?.length) params.set("entityIds", filter.entityIds.join(","));
        if (filter?.withoutControls) params.set("withoutControls", "true");
        if (filter?.overdue) params.set("overdue", "true");
        return `/privacy/risk/${riskAreaId}?${params.toString()}`;
    }

    function getRiskAssessmentTemplateUrl() {
        return `/privacy/risk-assessments/template`;
    }

    function getRiskAssessmentsPDF(id: string, riskAssessmentArea: RiskAssessmentArea, riskComplianceArea: RiskComplianceArea, versionId: string) {
        return `/privacy/risk-assessment/${id}/${riskAssessmentArea}/${riskComplianceArea}/${versionId}/pdf`;
    }

    function getProcessingActivityRiskScenariosDialog(id: string | undefined, versionId?: string) {
        return `/privacy/risk-assessments/${id}/${versionId}/scenarios`;
    }

    function getRiskAssessmentQuestionsDialog(
        id: string | undefined,
        riskAssessmentArea: RiskAssessmentArea,
        riskComplianceArea: RiskComplianceArea,
        versionId: string
    ) {
        if (!id) return `/privacy/risk-assessment`;
        return `/privacy/risk-assessment/${id}/${riskAssessmentArea}/${riskComplianceArea}/${versionId}/questions`;
    }

    function getSystemOverviewUrl(systemId: string | undefined, tab?: number) {
        const s = `/privacy/system/${systemId}/overview`;
        if (tab) {
            return s + `?tab=${tab}`;
        }
        return s;
    }

    function getSystemEditUrl(systemId?: string, step?: number) {
        const url = systemId ? `/privacy/system/${systemId}/edit` : `/privacy/system`;

        if (step && systemId) {
            return url + `?step=${step}`;
        }

        return url;
    }

    function getAnnualWheelUrl(queryString?: string) {
        const route = "/privacy/annual-wheel";
        if (!isNullOrWhitespace(queryString)) {
            return route + queryString;
        }

        return route;
    }

    function getActivitiesUrl(queryString?: string) {
        const route = "/privacy/activities";
        if (!isNullOrWhitespace(queryString)) {
            return route + queryString;
        }

        return route;
    }

    function getAnnualWheelUrlWithActivityId(annualWheelActivityId: string) {
        return getAnnualWheelUrl(`?activities=${annualWheelActivityId}`);
    }

    function getAnnualWheelPDFUrl() {
        return "/privacy/annual-wheel-pdf";
    }

    function getTaskManagementUrl(queryString?: string) {
        let url = "/privacy/task-management";
        if (isNullOrWhitespace(queryString)) {
            return url;
        }
        return url + queryString;
    }

    function getTaskManagementUrlForTask(month: AnnualWheelMonthEnum | undefined, id: string) {
        if (month === undefined) {
            return getTaskManagementUrl() + "?task=" + id;
        }
        return getTaskManagementUrl() + "?months=" + month.toString() + "&task=" + id;
    }

    function getTaskManagementTaskDialogUrl(taskId?: string, months?: number) {
        let queryString = `?taskId=${taskId}`;
        if (months !== undefined) {
            queryString = queryString + `&months=${months}`;
        }

        return getTaskManagementUrl(queryString);
    }

    function getTaskPdfUrl(taskId: string) {
        return getTaskManagementUrl(`/${taskId}/pdf`);
    }

    function getAddEditCustomerDialog(customerId: string) {
        return `/privacy/customer/${customerId}/edit`;
    }

    function getNotificationUrl() {
        return "/privacy/notifications";
    }

    function getSystemNotificationsUrl() {
        return "/privacy/settings/system-notifications";
    }

    function getCustomListViewWithOpenDialogUrl(id: string) {
        return "/privacy/custom-list-view/" + id + "?showDialog=true";
    }

    function getCustomListViewUrl(id: string) {
        return "/privacy/custom-list-view/" + id;
    }

    function getCustomProcessingActivityListsUrl() {
        return "/privacy/custom-processing-activity-lists";
    }

    function getCustomCompanyListsUrl() {
        return "/privacy/custom-company-lists";
    }

    function getCustomGroupCompanyListsUrl() {
        return "/privacy/custom-group-company-lists";
    }

    function getCustomSharingListsUrl() {
        return "/privacy/custom-sharing-lists";
    }

    function getUserRolesUrl() {
        return "/privacy/roles";
    }

    function getEditRolesUrl(roleId: string) {
        return `/privacy/role/${roleId}/edit-permission`;
    }

    function getPoliciesUrl() {
        return "/privacy/policies";
    }

    function getPolicyUrl(id: string) {
        return "/privacy/policy/" + id;
    }

    function getPolicyCompareUrl(policyId: string, fromVersionId: string, toVersionId: string) {
        return `/privacy/policy/${policyId}/compare/${fromVersionId}/${toVersionId}`;
    }

    function getProcessingActivityRegistrationOfResearchProjectReportPDF(id: string) {
        return `/privacy/processing-activity/${id}/registration-of-research-report-pdf`;
    }

    function getProcessingActivityInternalAcquisitionOfSignatureReportPDF(id: string | undefined) {
        return `/privacy/processing-activity/${id}/internal-acquisition-of-signature-report-pdf`;
    }

    function getProcessingActivityResearchEthicsApprovalReportReportPDF(id: string | undefined) {
        return `/privacy/processing-activity/${id}/research-ethics-approval-report-pdf`;
    }

    function getProcessingActivityApprovalOfQualityProjectReportPDF(id: string | undefined) {
        return `/privacy/processing-activity/${id}/approval-of-quality-project-report-pdf`;
    }

    function getProcessingActivityStatisticUrl() {
        return `/privacy/processing-activity-statistics`;
    }

    function getProcessingActivityStatisticPdfUrl() {
        return `/privacy/processing-activity-statistics/pdf`;
    }

    // The severity deep-links from the dashboard widget. The incident log reads it once on arrival, then strips it.
    function getIncidentLogUrl(filter?: { severity?: SeverityLevelEnum }) {
        if (filter?.severity !== undefined) {
            return `/privacy/incident-log?severity=${filter.severity}`;
        }
        return "/privacy/incident-log";
    }

    function getIncidentLogOverviewUrl(id: string, tab?: number) {
        const s = `/privacy/incident-log/${id}/overview`;
        if (tab) {
            return s + `?tab=${tab}`;
        }
        return s;
    }

    function getIncidentLogEditUrl(id: string, queryParams?: string) {
        const url = `/privacy/incident-log/${id}/edit`;

        return queryParams ? `${url}?${queryParams}` : url;
    }

    function getIncidentLogPdf(id: string) {
        return `/privacy/incident-log/${id}/pdf`;
    }

    function getAuditsUrl() {
        return "/privacy/questionnaires/audits";
    }

    function getVendorClassifications() {
        return "/privacy/questionnaires/vendor-classifications";
    }

    function getLegalEntityClassificationUrl(scope: LegalEntityScope, legalEntityId: string, responseId: string, redirectAfterSubmitUrl?: string) {
        const legalEntityBaseUrl = getLegalEntityBaseUrl(scope, legalEntityId);
        const redirectUrl = !isNullOrWhitespace(redirectAfterSubmitUrl)
            ? redirectAfterSubmitUrl
            : `${legalEntityBaseUrl + `?tab=${LegalEntityAuditTabs.Classifications}`}`;

        return `${legalEntityBaseUrl}/classification/response-internal/${responseId}/introduction?redirectAfterSubmitUrl=${redirectUrl}`;
    }

    function getQuestionnaireVendorClassificationOverview(questionnaireId: string) {
        return `/privacy/questionnaires/vendor-classifications/${questionnaireId}/overview`;
    }

    function getQuestionnaireAuditverview(questionnaireId: string) {
        return `/privacy/questionnaires/audits/${questionnaireId}/overview`;
    }

    function getQuestionnaireResponseReport(
        questionnaireResponseId: string,
        deadline: Date,
        senderName: string,
        respondent: string,
        pdf: boolean,
        showRecommendations: boolean
    ) {
        let url = `/privacy/questionnaires/no-menu/response-internal/${questionnaireResponseId}/report?deadline=${deadline}&sender=${encodeURIComponent(senderName)}&respondent=${encodeURIComponent(respondent)}&showRecommendations=${showRecommendations}`;
        if (pdf) {
            url = url + "&isPdf=true";
        }

        return url;
    }

    function getQuestionnaireResponseExternalreport(
        questionnaireexternalId: string,
        deadline: Date,
        senderName: string,
        respondent: string,
        assessmentTitle: string | undefined,
        pdf: boolean,
        showRecommendations: boolean,
        includeScoreForInternalAssessments: boolean
    ) {
        const nameParameter = assessmentTitle ? `&name=${encodeURIComponent(assessmentTitle)}` : ``;

        let url = `/privacy/questionnaires/external/response-external/${questionnaireexternalId}/report?deadline=${deadline}&sender=${encodeURIComponent(senderName)}&respondent=${encodeURIComponent(respondent)}${nameParameter}&showRecommendations=${showRecommendations}&includeScoreForInternalAssessments=${includeScoreForInternalAssessments}`;
        if (pdf) {
            url = url + "&isPdf=true";
        }
        return url;
    }

    function getClassificationQuestionnaireResponseReport(questionnaireResponseId: string, respondent: string, pdf: boolean, showRecommendations: boolean) {
        let url = `/privacy/questionnaires/no-menu/response-internal/${questionnaireResponseId}/report?respondent=${encodeURIComponent(respondent)}&showRecommendations=${showRecommendations}`;
        if (pdf) {
            url = url + "&isPdf=true";
        }
        return url;
    }

    function getInternalAssessmentQuestionnaireResponseReport(
        questionnaireResponseId: string,
        deadline: Date,
        senderName: string,
        assesmentTille: string,
        pdf: boolean,
        showRecommendations: boolean,
        respondent: string
    ) {
        let url = `/privacy/questionnaires/no-menu/response-internal/${questionnaireResponseId}/report?deadline=${deadline}&sender=${encodeURIComponent(senderName)}&name=${encodeURIComponent(assesmentTille)}&showRecommendations=${showRecommendations}&respondent=${encodeURIComponent(respondent)}`;
        if (pdf) {
            url = url + "&isPdf=true";
        }
        return url;
    }

    function getInternalAssessmentUrl() {
        return "/privacy/internal-assessments";
    }

    function getPublicStartInternalAssessmentUrl(externalId: string) {
        return `/privacy/start-internal-assessment/${externalId}`;
    }

    function getPublicReportObservationUrl(externalId: string) {
        return `/privacy/public/report-observation/${externalId}`;
    }

    function getExternalQuestionnaireResponseUrl(externalId: string) {
        return `/privacy/questionnaires/external/response-external/${externalId}/introduction`;
    }

    function getAuditGroupsUrl() {
        return `/privacy/audit-groups/`;
    }

    function getAuditGroupUrl(auditGroupId: string) {
        return `/privacy/audit-group/${auditGroupId}`;
    }

    function getAuditGroupDraftUrl(auditGroupId: string) {
        return `/privacy/audit-group/${auditGroupId}/draft`;
    }

    function getInternalAssessmentGroupsUrl() {
        return `/privacy/internal-assessments?view=groups`;
    }

    function getInternalAssessmentGroupUrl(internalAssessmentGroupId: string) {
        return `/privacy/assessment-group/${internalAssessmentGroupId}`;
    }

    function getInternalAssessmentGroupDraftUrl(internalAssessmentGroupId: string) {
        return `/privacy/assessment-group/${internalAssessmentGroupId}/draft`;
    }

    function getAuditGroupManagementReportUrl(auditGroupId: string) {
        return `/privacy/audit-group/${auditGroupId}/management-report`;
    }

    function getInternalAssessmentGroupManagementReportUrl(internalAssessmentGroupId: string) {
        return `/privacy/assessment-group/${internalAssessmentGroupId}/management-report`;
    }

    function getDotLegalVendorMangementInfoUrl() {
        return "https://www.dotlegal.com/en/product/vendor-management-software";
    }

    function getDashboardUrl() {
        return `/privacy/dashboard`;
    }

    function getContractDashboardUrl() {
        return `/privacy/dashboard/contracts`;
    }

    function getFrameworksUrl() {
        return `/privacy/frameworks`;
    }

    function getFrameworkOverviewUrl(id: string) {
        return `/privacy/frameworks/${id}/overview`;
    }

    function getFrameworkCategoriesUrl(id: string) {
        return `/privacy/frameworks/${id}/categories`;
    }

    function getFrameworkControlOverviewUrl(id: string) {
        return `/privacy/framework-controls/${id}/overview`;
    }

    function getComplianceReportUrl(id: string) {
        return `/privacy/dashboard/compliance-report/${id}`;
    }

    function getTagsUrl() {
        return "/privacy/masterdata/tags";
    }

    function getDocumentTypesUrl() {
        return "/privacy/masterdata/document-types";
    }

    function getIncidentTypesUrl() {
        return "/privacy/masterdata/incident-types";
    }

    function getIncidentAffectedServicesUrl() {
        return "/privacy/masterdata/incident-affected-services";
    }

    function getDotLegalContactForm() {
        return "https://www.dotlegal.com/en/about/contact-us";
    }

    function getPurchasePlansUrl() {
        const settings = getSettings();

        let returnUrl = window.location.href;
        return settings.customerAdminWeb + `/purchase?productCustomerId=${customerId}&returnUrl=${returnUrl}`;
    }

    function getContractsUrl(deepLink?: ContractListDeepLink) {
        return "/privacy/contracts" + (deepLink ? toContractListSearch(deepLink) : "");
    }

    function getContractOverviewUrl(id: string, tab?: ContractOverviewTabEnum) {
        let url = `/privacy/contracts/${id}/overview`;
        if (tab) {
            url = url + `?tab=${tab}`;
        }
        return url;
    }

    function getLegalEntityTaskApiUrl(legalEntityId: string) {
        return `/relations/${legalEntityId}/tasks/legalEntity`;
    }

    function getAuditGroupTaskApiUrl(auditGroupId: string) {
        return `/relations/${auditGroupId}/tasks/auditGroup`;
    }

    function getInternalAssessmentGroupTaskApiUrl(internalAssessmentGroupId: string) {
        return `/relations/${internalAssessmentGroupId}/tasks/internalAssessmentGroup`;
    }

    function getContractTaskApiUrl(contractId: string) {
        return `/contract/${contractId}/tasks`;
    }

    function getContractTypesUrl() {
        return `/privacy/masterdata/contract-types/`;
    }

    function getLegalEntityTypesUrl() {
        return `/privacy/masterdata/legal-entity-types/`;
    }

    function getPaymentTermsUrl() {
        return `/privacy/masterdata/payment-terms/`;
    }

    function getContractUrl(id: string, step?: number, addCreateQueryParam: boolean = false) {
        const baseUrl = `/privacy/contracts/${id}/edit`;

        const params = new URLSearchParams();
        if (addCreateQueryParam) params.append("create", "true");
        if (step !== undefined) params.append("step", step.toString());

        const queryString = params.toString();
        return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    }

    function getLegalEntityRiskAuditReportUrl(id: string, scope: LegalEntityScope) {
        return `/privacy/riskauditreport/${id}/${scope}`;
    }

    function getLegalEntityRiskClassificationReportUrl(id: string, scope: LegalEntityScope) {
        return `/privacy/riskclassificationreport/${id}/${scope}`;
    }

    function getFrameworkTemplateOverviewUrl(id: string) {
        return `/privacy/framework-templates/${id}`;
    }

    function getFrameworkTemplatesUrl() {
        return `/privacy/framework-templates`;
    }

    function getFrameworkTemplateCategoriesUrl(id: string) {
        return `/privacy/framework-templates/${id}/categories`;
    }

    function getObligationTemplatesUrl() {
        return "/privacy/masterdata/obligation-templates";
    }

    function getContractInputFieldsUrl() {
        return "/privacy/settings/contract-input-fields";
    }
    function getSystemInputFieldsUrl() {
        return "/privacy/settings/asset-input-fields";
    }

    function getLegalEntityInputFieldsUrl() {
        return "/privacy/settings/legal-entity-input-fields";
    }

    function getIncidentLogInputFieldsUrl() {
        return "/privacy/settings/incident-log-input-fields";
    }

    function getProcessInputFieldsUrl() {
        return "/privacy/settings/process-input-fields";
    }

    function getEntityTemplates(type: TemplateEntityType) {
        return "/privacy/settings/entity-templates/" + type;
    }

    function getEditEntityTemplate(id: string) {
        return `/privacy/settings/entity-templates/${id}/edit`;
    }

    function getEditEntityTemplateSteps(id: string) {
        return `/privacy/settings/entity-templates/${id}/steps`;
    }

    function getInputTypeHelpCenterUrl() {
        return "https://help.dotlegal.com/en/knowledge-base/create-custom-input-fields-to-contract-templates";
    }

    function getAdvancedDocumentSearchUrl() {
        return "/privacy/advanced-document-search";
    }

    function getAdministrativeFunctionsUrl() {
        return "/privacy/settings/administrative-functions";
    }

    return {
        getMasterDataUrl,
        getEmailCustomizationUrl,
        getAiAdministrationUrl,
        getApiKeysUrl,
        getEditProcessingActivityUrl,
        getProcessingActivityOverviewUrl,
        getProcessingActivityOverviewWithOpenCommenDialogUrl,
        getEditProcessingActivityUrlCreateView,
        getLegalEntityUrl,
        getProcessingActivitiesOverviewUrl,
        getDataProcessingUrl,
        getProcessesUrl,
        getProcessOverviewUrl,
        getProcessEditUrl,
        getRecordUrl,
        getSignOutUrl,
        getHelpUrl,
        getGroupEntitiesUrl,
        getSettingsUrl,
        getBestPracticeDocumentTemplatesUrl,
        getGroupEntityUrl,
        getUsersUrl,
        getUserUrl,
        getCustomersUrl,
        getExternalUsersUrl,
        getChangeOrganizationUrl,
        getForbiddenUrl,
        getPurposesUrl,
        getSecurityMeasures,
        getDeletionPeriods,
        getBusinessAreasUrl,
        getDataCategories,
        getDataSubjectsUrl,
        getNationalLawsUrl,
        getRiskAssessmentsUrl,
        getRiskManagementReportUrl,
        getObservationsUrl,
        getRiskManagementReportPdfUrl,
        getRiskGovernanceOverviewPdfUrl,
        getRiskAreaUrl,
        getRiskOperationalOverviewUrl,
        getRiskAssessmentsOverviewUrl,
        getRiskMitigationOverviewUrl,
        getRiskAssessmentsPDF,
        getProcessingActivityRiskScenariosDialog,
        getRiskAssessmentQuestionsDialog,
        getRiskAssessmentTemplateUrl,
        getSystemsUrl,
        getSystemOverviewUrl,
        getSystemCreateViewUrl,
        getSystemEditUrl,
        getLegislationsUrl,
        getAnnualWheelUrl,
        getAnnualWheelPDFUrl,
        getTaskManagementUrl,
        getTaskManagementUrlForTask,
        getAddEditCustomerDialog,
        getNotificationUrl,
        getCustomListViewUrl,
        getCustomProcessingActivityListsUrl,
        getCustomCompanyListsUrl,
        getCustomSharingListsUrl,
        getCustomListViewWithOpenDialogUrl,
        getUserRolesUrl,
        getEditRolesUrl,
        getPoliciesUrl,
        getPolicyUrl,
        getPolicyCompareUrl,
        getHelpTextsUrl,
        getProcessingActivityRegistrationOfResearchProjectReportPDF,
        getProcessingActivityInternalAcquisitionOfSignatureReportPDF,
        getProcessingActivityResearchEthicsApprovalReportReportPDF,
        getProcessingActivityStatisticUrl,
        getProcessingActivityStatisticPdfUrl,
        getCustomGroupCompanyListsUrl,
        getCertificationsUrl,
        getAreasUrl,
        getRiskAreasUrl,
        getRiskScenariosUrl,
        getConsequencesUrl,
        getRiskAssessmentVersionsUrl,
        getIncidentLogUrl,
        getIncidentLogOverviewUrl,
        getIncidentLogEditUrl,
        getAuditsUrl,
        getQuestionnaireResponseReport,
        getExternalLegalEntities,

        getLegalEntityBaseUrl,
        getLegalEntityEditUrl,
        getVendorClassifications,
        getLegalEntityClassificationUrl,
        getQuestionnaireVendorClassificationOverview,
        getQuestionnaireAuditOverview: getQuestionnaireAuditverview,
        getClassificationQuestionnaireResponseReport,
        getInternalAssessmentUrl,
        getInternalAssessmentQuestionnaireResponseReport,
        getProcessingActivityValidationUrl,
        getPublicStartInternalAssessmentUrl,
        getPublicReportObservationUrl,
        getQuestionnaireResponseExternalreport,
        getExternalQuestionnaireResponseUrl,
        getAuditGroupsUrl,
        getAuditGroupUrl,
        getAuditGroupDraftUrl,
        getAuditGroupManagementReportUrl,
        getInternalAssessmentGroupManagementReportUrl,
        getInternalAssessmentGroupsUrl,
        getInternalAssessmentGroupUrl,
        getInternalAssessmentGroupDraftUrl,
        getTaskManagementTaskDialogUrl,
        getDotLegalVendorMangementInfoUrl,
        getDashboardUrl,
        getContractDashboardUrl,
        getFrameworksUrl,
        getFrameworkOverviewUrl,
        getComplianceReportUrl,
        getTagsUrl,
        getDotLegalContactForm,
        getDataTypesUrl,
        getDocumentTypesUrl,
        getIncidentTypesUrl,
        getIncidentAffectedServicesUrl,
        getPurchasePlansUrl,
        getContractsUrl,
        getContractOverviewUrl,
        getLegalEntityTaskApiUrl,
        getAuditGroupTaskApiUrl,
        getInternalAssessmentGroupTaskApiUrl,
        getAnnualWheelUrlWithActivityId,
        getContractTypesUrl,
        getLegalEntityTypesUrl,
        getPaymentTermsUrl,
        getContractUrl,
        getIncidentLogPdf,
        getLegalEntityRiskAuditReportUrl,
        getLegalEntityRiskClassificationReportUrl,
        getTaskPdfUrl,
        getFrameworkTemplateOverviewUrl,
        getFrameworkTemplatesUrl,
        getFrameworkTemplateCategoriesUrl,
        getAnnualWheelActivityTemplatesUrl,
        getAnnualWheelActivityStandardTemplatesUrl,
        getFrameworkCategoriesUrl,
        getFrameworkControlOverviewUrl,
        getObligationTemplatesUrl,
        getInputFieldsUrl: getContractInputFieldsUrl,
        getEntityTemplates,
        getEditEntityTemplate,
        getEditEntityTemplateSteps,
        getInputTypeHelpCenterUrl,
        getProcessingActivityApprovalOfQualityProjectReportPDF,
        getContractTaskApiUrl,
        getAdvancedDocumentSearchUrl,
        getSystemNotificationsUrl,
        getAdministrativeFunctionsUrl,
        getActivitiesUrl,
        getSystemInputFieldsUrl,
        getLegalEntityInputFieldsUrl,
        getIncidentLogInputFieldsUrl,
        getProcessInputFieldsUrl,
    };
}
